import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format, differenceInCalendarDays } from "date-fns";
import { it } from "date-fns/locale";

import { api, errMsg } from "../lib/api";
import { useAuth } from "../stores/auth";
import { usePrefs } from "../stores/prefs";
import { beep, vibrate } from "../lib/sound";

import { TopBar } from "../components/ui/TopBar";
import { Icon } from "../components/ui/Icon";
import { Button } from "../components/ui/Button";
import { Banner } from "../components/ui/Banner";
import { Badge } from "../components/ui/Badge";
import { SectionTitle } from "../components/ui/SectionTitle";
import { ProductThumb } from "../components/ui/ProductThumb";

type Line = {
  id: number;
  sku: string;
  name: string | null;
  image_url: string | null;
  qty_out: number;
  qty_distributed: Record<string, number>;
  qty_back_to_sede: number;
  is_reconciled: boolean;
};

type Location = {
  code: string;
  name: string;
  city: string | null;
  type: string;
  active: boolean;
};

type Transfer = {
  id: number;
  operator: string;
  status: string;
  expected_locations: string[] | null;
  qr_bag_code: string | null;
  opened_at: string;
  closed_at: string | null;
  lines: Line[];
  notes?: string | null;
};

export function TransferIn() {
  const user = useAuth((s) => s.user);
  const [params] = useSearchParams();
  const focus = params.get("focus");
  const nav = useNavigate();
  const [selected, setSelected] = useState<number | null>(focus ? Number(focus) : null);

  const { data: openTransfers, refetch } = useQuery<Transfer[]>({
    queryKey: ["transfers", "open", user?.username],
    queryFn: async () =>
      (
        await api.get("/api/transfer/open", {
          params: { operator: user?.username },
        })
      ).data,
    enabled: !!user,
  });

  const { data: current } = useQuery<Transfer>({
    queryKey: ["transfer", selected],
    queryFn: async () => (await api.get(`/api/transfer/${selected}`)).data,
    enabled: !!selected,
  });

  const { data: locations } = useQuery<Location[]>({
    queryKey: ["locations"],
    queryFn: async () => (await api.get("/api/locations")).data,
  });
  const locNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const l of locations ?? []) map[l.code] = l.name;
    return map;
  }, [locations]);

  if (!selected) {
    return (
      <>
        <TopBar back onBack={() => nav("/")} title="Transfer In" sub="Viaggi aperti" />

        <SectionTitle
          right={
            <span style={{ paddingRight: 18, fontSize: 11, color: "var(--tcg-muted)", fontWeight: 700 }}>
              <span className="tcg-num">{(openTransfers ?? []).length}</span> in corso
            </span>
          }
        >
          I tuoi viaggi
        </SectionTitle>

        <div
          style={{
            padding: "0 18px",
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          {(openTransfers ?? []).length === 0 && (
            <div className="tcg-card" style={{ padding: 16, textAlign: "center" }}>
              <Icon name="truck" size={32} />
              <div style={{ marginTop: 8, fontSize: 13, fontWeight: 700 }}>Nessun viaggio aperto</div>
              <div style={{ fontSize: 11, color: "var(--tcg-muted)", marginTop: 4 }}>
                Apri un nuovo refill da "Transfer Out".
              </div>
              <Button variant="primary" onClick={() => nav("/transfer-out")} style={{ marginTop: 12 }}>
                <Icon name="plus" size={14} /> Apri viaggio
              </Button>
            </div>
          )}

          {(openTransfers ?? []).map((t) => {
            const days = differenceInCalendarDays(new Date(), new Date(t.opened_at));
            const totalOut = t.lines.reduce((s, l) => s + l.qty_out, 0);
            return (
              <button
                key={t.id}
                className="tcg-card"
                onClick={() => setSelected(t.id)}
                style={{
                  padding: 14,
                  textAlign: "left",
                  cursor: "pointer",
                  background: "var(--tcg-card)",
                  display: "block",
                  width: "100%",
                  border: "1px solid var(--tcg-border)",
                  fontFamily: "inherit",
                  color: "inherit",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <div>
                    <div
                      className="tcg-mono"
                      style={{ fontSize: 10, color: "var(--tcg-muted)", fontWeight: 700 }}
                    >
                      {t.qr_bag_code || `TRP-${t.id}`}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 800, marginTop: 2 }}>
                      {t.lines.length} SKU · {totalOut} pezzi
                    </div>
                  </div>
                  <Badge tone={days > 2 ? "warn" : "navy"}>{days === 0 ? "OGGI" : `${days} GG`}</Badge>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    marginTop: 8,
                    flexWrap: "wrap",
                  }}
                >
                  {(t.expected_locations ?? []).map((code) => (
                    <span
                      key={code}
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: "var(--tcg-navy)",
                        background: "var(--tcg-bg)",
                        padding: "3px 8px",
                        borderRadius: 6,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                      }}
                    >
                      <Icon name="pin" size={10} />
                      {locNameMap[code] || code}
                    </span>
                  ))}
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: 10,
                    paddingTop: 10,
                    borderTop: "1px dashed var(--tcg-border)",
                  }}
                >
                  <div style={{ fontSize: 11, color: "var(--tcg-muted)", fontWeight: 600 }}>
                    Aperto {format(new Date(t.opened_at), "dd MMM 'alle' HH:mm", { locale: it })}
                  </div>
                  <span style={{ color: "var(--tcg-navy)", fontWeight: 800, fontSize: 12 }}>
                    Apri →
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        <div style={{ height: 24 }} />
      </>
    );
  }

  if (!current) {
    return (
      <>
        <TopBar back onBack={() => setSelected(null)} title="Transfer In" />
        <div className="tcg-card" style={{ margin: 18, padding: 16, textAlign: "center" }}>
          Caricamento viaggio…
        </div>
      </>
    );
  }

  return (
    <TransferEditor
      transfer={current}
      onBack={() => {
        setSelected(null);
        nav("/transfer-in", { replace: true });
      }}
      onClosed={async () => {
        await refetch();
        setSelected(null);
        nav("/transfer-in", { replace: true });
      }}
    />
  );
}

type Row = {
  sku: string;
  name: string | null;
  image_url: string | null;
  qty_out: number;
  back: number;
  note: string;
};

type EditorProps = {
  transfer: Transfer;
  onBack: () => void;
  onClosed: () => Promise<void> | void;
};

function TransferEditor({ transfer, onBack, onClosed }: EditorProps) {
  const qc = useQueryClient();
  const prefs = usePrefs((s) => s.prefs);

  const initial: Row[] = useMemo(
    () =>
      transfer.lines.map((l) => ({
        sku: l.sku,
        name: l.name,
        image_url: l.image_url,
        qty_out: l.qty_out,
        back: l.qty_back_to_sede,
        note: "",
      })),
    [transfer],
  );
  const [rows, setRows] = useState<Row[]>(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => setRows(initial), [initial]);

  const totals = useMemo(() => {
    const totalOut = rows.reduce((s, r) => s + r.qty_out, 0);
    const back = rows.reduce((s, r) => s + r.back, 0);
    const inMachines = totalOut - back;
    // A row is invalid only if returns exceed what went out.
    const invalid = rows.filter((r) => r.back > r.qty_out).length;
    return { totalOut, back, inMachines, invalid };
  }, [rows]);

  function setBack(idx: number, raw: string) {
    const value = Math.max(0, parseInt(raw || "0", 10) || 0);
    setRows((rs) =>
      rs.map((r, i) => (i === idx ? { ...r, back: Math.min(value, r.qty_out) } : r)),
    );
  }
  function setNote(idx: number, note: string) {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, note } : r)));
  }

  async function submit() {
    if (totals.invalid > 0 || busy) return;
    setBusy(true);
    setErr(null);
    const returns = rows.map((r) => ({ sku: r.sku, qty_back: r.back }));
    const notesCombined = rows
      .filter((r) => r.note.trim())
      .map((r) => `${r.sku}: ${r.note.trim()}`)
      .join("\n");
    try {
      await api.post(`/api/transfer/${transfer.id}/close`, {
        returns,
        notes: notesCombined || null,
      });
      if (prefs.beep) beep("ok");
      if (prefs.vibrate) vibrate([20, 30, 20]);
      await qc.invalidateQueries({ queryKey: ["overview"] });
      await qc.invalidateQueries({ queryKey: ["transfers"] });
      await onClosed();
    } catch (e) {
      if (prefs.beep) beep("error");
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  const days = differenceInCalendarDays(new Date(), new Date(transfer.opened_at));

  return (
    <>
      <TopBar back onBack={onBack} title="Transfer In" sub="Rientro in sede · ricostruzione" />

      <div style={{ padding: "14px 18px 0" }}>
        <div
          style={{
            background: "linear-gradient(135deg, var(--tcg-navy) 0%, var(--tcg-navy-dark) 100%)",
            color: "var(--tcg-cream)",
            borderRadius: 18,
            padding: 14,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 10,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 10,
                  color: "var(--tcg-yellow)",
                  fontWeight: 800,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                ↙ Viaggio attivo · in chiusura
              </div>
              <div
                className="tcg-mono"
                style={{ fontSize: 10, color: "rgba(255,255,255,0.6)", fontWeight: 700, marginTop: 2 }}
              >
                {transfer.qr_bag_code || `TRP-${transfer.id}`}
              </div>
            </div>
            <div
              style={{
                background: "var(--tcg-yellow)",
                color: "var(--tcg-navy-deep)",
                fontSize: 9,
                fontWeight: 900,
                padding: "4px 8px",
                borderRadius: 6,
                letterSpacing: "0.06em",
              }}
            >
              {days === 0 ? "OGGI" : `${days} GG APERTO`}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            <KpiCol label="Uscita" value={totals.totalOut} />
            <KpiCol label="Rientro sede" value={totals.back} highlight />
            <KpiCol label="In macchinetta" value={totals.inMachines} />
          </div>
        </div>
      </div>

      <SectionTitle
        right={
          <div
            style={{
              paddingRight: 18,
              fontSize: 10,
              color: "var(--tcg-muted)",
              fontWeight: 700,
              letterSpacing: "0.04em",
            }}
          >
            RIENTRI IN SEDE
          </div>
        }
      >
        Riconciliazione SKU
      </SectionTitle>

      <div style={{ padding: "0 18px", display: "flex", flexDirection: "column", gap: 10 }}>
        {rows.map((r, idx) => {
          const inMachine = r.qty_out - r.back;
          const invalid = r.back > r.qty_out;
          return (
            <div
              key={r.sku}
              className="tcg-card"
              style={{
                padding: 12,
                borderColor: invalid ? "var(--tcg-danger)" : "var(--tcg-border)",
                borderWidth: invalid ? 1.5 : 1,
              }}
            >
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10 }}>
                <ProductThumb imageUrl={r.image_url} sku={r.sku} size="xs" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 800,
                      lineHeight: 1.25,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {r.name || inferCategory(r.sku).label}
                  </div>
                  <div
                    className="tcg-mono"
                    style={{ fontSize: 10, color: "var(--tcg-muted)", fontWeight: 600, marginTop: 1 }}
                  >
                    {r.sku}
                  </div>
                </div>
                <div style={{ flexShrink: 0, textAlign: "right" }}>
                  <div
                    style={{
                      fontSize: 9,
                      color: "var(--tcg-muted)",
                      fontWeight: 800,
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                    }}
                  >
                    Uscita
                  </div>
                  <div className="tcg-num" style={{ fontSize: 18, fontWeight: 900, lineHeight: 1, marginTop: 2 }}>
                    {r.qty_out}
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  paddingTop: 6,
                  borderTop: "1px dashed var(--tcg-border)",
                }}
              >
                <div
                  style={{
                    flex: 1,
                    fontSize: 11,
                    color: "var(--tcg-ink-soft)",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <Icon name="box" size={12} /> Rientri in sede
                </div>
                <input
                  className="tcg-input tcg-num"
                  type="number"
                  min={0}
                  max={r.qty_out}
                  value={r.back}
                  onChange={(e) => setBack(idx, e.target.value)}
                  onFocus={(e) => e.currentTarget.select()}
                  style={{
                    width: 74,
                    padding: "8px 10px",
                    textAlign: "center",
                    fontSize: 14,
                    fontWeight: 800,
                    color: "var(--tcg-navy)",
                    background: "var(--tcg-bg)",
                  }}
                />
              </div>

              <div
                style={{
                  marginTop: 10,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  justifyContent: "space-between",
                }}
              >
                <div style={{ fontSize: 10, color: "var(--tcg-muted)", fontWeight: 700 }}>
                  Rimaste in macchinetta:{" "}
                  <span
                    className="tcg-num"
                    style={{ color: "var(--tcg-ink)", fontWeight: 800 }}
                  >
                    {Math.max(0, inMachine)}
                  </span>
                </div>
                {invalid ? (
                  <Badge tone="danger">⚠ rientri &gt; uscite</Badge>
                ) : (
                  <Badge tone="navy">
                    <Icon name="check" size={10} /> {r.back} in sede
                  </Badge>
                )}
              </div>

              <input
                className="tcg-input"
                value={r.note}
                onChange={(e) => setNote(idx, e.target.value)}
                placeholder="Note (opzionale · es. layer rotto, residuo danneggiato…)"
                style={{ marginTop: 10, fontSize: 12, padding: "9px 12px" }}
              />
            </div>
          );
        })}
      </div>

      <div style={{ padding: "16px 18px 0" }}>
        {err && (
          <div style={{ marginBottom: 10 }}>
            <Banner tone="danger" icon="x">
              {err}
            </Banner>
          </div>
        )}
        {totals.invalid > 0 && (
          <Banner tone="warn" icon="flag">
            <strong>{totals.invalid} SKU con rientri &gt; uscite.</strong>{" "}
            Correggi le quantità prima di chiudere il viaggio.
          </Banner>
        )}
        <Button
          variant="yellow"
          block
          disabled={busy || totals.invalid > 0}
          onClick={submit}
          style={{ marginTop: 10 }}
        >
          <Icon name="check" size={16} />
          {busy ? "Chiusura…" : `Chiudi viaggio · ${rows.length} SKU`}
        </Button>
      </div>

      <div style={{ height: 24 }} />
    </>
  );
}

function KpiCol({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div>
      <div
        style={{
          fontSize: 9,
          color: "rgba(255,255,255,0.55)",
          fontWeight: 800,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div
        className="tcg-num"
        style={{
          fontSize: 18,
          fontWeight: 900,
          color: highlight ? "var(--tcg-yellow)" : "var(--tcg-cream)",
        }}
      >
        {value}
      </div>
    </div>
  );
}

type CategoryMeta = {
  label: string;
  thumbBg: string;
  thumbFg: string;
  thumbLabel: string;
};

function inferCategory(sku: string): CategoryMeta {
  const first = sku.split("-")[0]?.toUpperCase() || "";
  switch (first) {
    case "POK":
      return { label: "Pokémon", thumbBg: "#FFE4D6", thumbFg: "#9A3412", thumbLabel: "POK" };
    case "OP":
      return { label: "One Piece", thumbBg: "#DBEAFE", thumbFg: "#1E40AF", thumbLabel: "OP" };
    case "YGO":
      return { label: "Yu-Gi-Oh!", thumbBg: "#FCE7F3", thumbFg: "#9D174D", thumbLabel: "YGO" };
    case "LOR":
      return { label: "Lorcana", thumbBg: "#FFEDD5", thumbFg: "#C2410C", thumbLabel: "LOR" };
    case "FUN":
      return { label: "Funko", thumbBg: "#CCFBF1", thumbFg: "#0F766E", thumbLabel: "FUN" };
    case "DB":
      return { label: "Dragon Ball", thumbBg: "#FEF3C7", thumbFg: "#92400E", thumbLabel: "DB" };
    case "MTG":
      return { label: "Magic", thumbBg: "#E6EBF8", thumbFg: "#1B3A8C", thumbLabel: "MTG" };
    default:
      return { label: "Altro", thumbBg: "#F4F1EA", thumbFg: "#3E4255", thumbLabel: first.slice(0, 3) || "???" };
  }
}
